import React from 'react';
import {
  SkeletonCta,
  SkeletonHero,
  SkeletonLine,
  SkeletonProgress,
  SkeletonTextLines,
} from '../Components/skeleton/SkeletonPieces';

/**
 * The embed's detail page while it loads (EmbedFormDetailPage, a form whose
 * first step is the campaign): the flush 16:9 hero, the title, the
 * fundraising bar, the description lines, Learn more, and Give pinned to the
 * bottom of the fixed-height card.
 *
 * It renders the detail page's own card classes (`.dynamic-detail-height` is
 * the card's fixed height at each container width), so the card's edges are
 * the real card's.
 *
 * `statusProps` carries the surface's single `role="status"` when this frame
 * is the announcing element (FormSkeleton passes it); standing alone it
 * announces itself.
 */
const DetailPageSkeleton = ({ formDisplayMode, isHostedPage = false, isEvent = false, statusProps } = {}) => {
  const isOverlay = formDisplayMode === 'overlay';
  const status = statusProps ?? {
    role: 'status',
    'aria-live': 'polite',
    'aria-label': 'Loading campaign details',
  };

  return (
    <div
      className={`page active ${isOverlay ? 'flex w-full flex-1 flex-col' : 'container mx-auto h-full max-w-md items-center'}`}
      {...status}
    >
      {status.role && <span className="sr-only">{status['aria-label']}, please wait...</span>}
      <div
        className={isOverlay ? 'flex flex-1 flex-col' : isHostedPage ? 'hosted-spacing-wrapper' : 'dynamic-spacing-wrapper'}
        style={{ ...(isHostedPage ? {} : { padding: isOverlay ? '0' : '24px 0' }), margin: '0', boxSizing: 'border-box' }}
      >
        <div
          className={`relative flex flex-col overflow-hidden ${isOverlay ? 'flex-1' : `frosted-background cs-form-card ${isEvent ? 'event-detail-height' : 'dynamic-detail-height'}`} ${!isOverlay && !isHostedPage ? 'dynamic-frosted-margin' : ''}`}
          aria-hidden="true"
        >
          <div className="cs-form-card-media shrink-0 overflow-hidden">
            <SkeletonHero flush />
          </div>
          <div className="cs-form-card-body flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="flex min-h-0 flex-1 flex-col">
              <SkeletonLine leading="var(--cs-leading-30)" className="w-3/4" />
              <div className="mt-[var(--cs-space-4)]">
                <SkeletonProgress />
              </div>
              <SkeletonTextLines count={3} gap="gap-[var(--cs-space-1)]" className="mt-[var(--cs-space-4)]" />
              <SkeletonLine className="mt-[var(--cs-space-3)] w-20" />
            </div>
            <SkeletonCta className="pt-[var(--cs-space-3)]" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailPageSkeleton;
